import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonItem,
  IonInput,
  IonLabel,
  IonIcon,
  IonBackButton,
  IonButtons,
  IonToast,
  IonList,
  IonSelect,
  IonSelectOption,
  IonToggle,
  IonSpinner,
} from '@ionic/react';
import { save } from 'ionicons/icons';
import { z } from 'zod';
import { workTypeEnum } from '@/lib/validation/registration';
import { NoindexSeo } from '@/components/NoindexSeo';
import { AppFooter } from '@/components/AppFooter';
import { useMyCandidateProfile, useUpdateCandidateProfile } from '@/lib/api/hooks';

const prefSchema = z.object({
  workType: z.enum(workTypeEnum),
  salaryMin: z.number().min(0).optional(),
  salaryMax: z.number().min(0).optional(),
  industry: z.string().optional(),
  remotePreference: z.enum(['OnSite', 'Hybrid', 'Remote', 'Any']),
  willingToRelocate: z.boolean(),
});

interface PrefForm {
  workType: string;
  salaryMin: string;
  salaryMax: string;
  industry: string;
  remotePreference: string;
  willingToRelocate: boolean;
}

export function PreferenceForm() {
  const history = useHistory();
  const { data: envelope, isLoading } = useMyCandidateProfile();
  const profile = envelope?.data;
  const updateProfile = useUpdateCandidateProfile();

  const [form, setForm] = useState<PrefForm>({
    workType: 'Contract',
    salaryMin: '',
    salaryMax: '',
    industry: '',
    remotePreference: 'OnSite',
    willingToRelocate: false,
  });

  const [initialized, setInitialized] = useState(false);
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null);

  useEffect(() => {
    if (profile && !initialized) {
      setForm({
        workType: profile.workType ?? 'Contract',
        salaryMin: profile.desiredSalaryMin?.toString() ?? '',
        salaryMax: profile.desiredSalaryMax?.toString() ?? '',
        industry: profile.industry ?? '',
        remotePreference: profile.remotePreference ?? 'OnSite',
        willingToRelocate: profile.willingToRelocate ?? false,
      });
      setInitialized(true);
    }
  }, [profile, initialized]);

  const set = (patch: Partial<PrefForm>) => setForm((f) => ({ ...f, ...patch }));

  const handleSave = () => {
    const r = prefSchema.safeParse({
      workType: form.workType,
      salaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
      salaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
      industry: form.industry || undefined,
      remotePreference: form.remotePreference,
      willingToRelocate: form.willingToRelocate,
    });
    if (!r.success) {
      const e: Record<string, string> = {};
      for (const issue of r.error.issues) e[String(issue.path[0])] = issue.message;
      setToast({ msg: Object.values(e).join(', '), color: 'danger' });
      return;
    }

    if (!profile?._id) {
      setToast({ msg: 'No profile found', color: 'danger' });
      return;
    }

    updateProfile.mutate(
      {
        id: profile._id,
        data: {
          workType: form.workType as typeof profile.workType,
          desiredSalaryMin: form.salaryMin ? Number(form.salaryMin) : undefined,
          desiredSalaryMax: form.salaryMax ? Number(form.salaryMax) : undefined,
          industry: form.industry || undefined,
          remotePreference: form.remotePreference as typeof profile.remotePreference,
          willingToRelocate: form.willingToRelocate,
        },
      },
      {
        onSuccess: () => {
          setToast({ msg: 'Preferences saved', color: 'success' });
          setTimeout(() => history.goBack(), 800);
        },
        onError: (err: Error) => {
          setToast({ msg: err.message || 'Failed to save preferences', color: 'danger' });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <IonPage>
        <NoindexSeo />
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/profile" /></IonButtons>
            <IonTitle>Job Preferences</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding" style={{ textAlign: 'center', paddingTop: '3rem' }}>
          <IonSpinner name="crescent" />
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <NoindexSeo />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/profile" />
          </IonButtons>
          <IonTitle>Job Preferences</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} disabled={updateProfile.isPending}>
              {updateProfile.isPending ? <IonSpinner name="crescent" style={{ width: 20, height: 20 }} /> : <IonIcon icon={save} />}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonList lines="full">
          <IonItem>
            <IonSelect label="Work type" value={form.workType} onIonChange={(e) => set({ workType: e.detail.value })}>
              {workTypeEnum.map((w) => <IonSelectOption key={w} value={w}>{w}</IonSelectOption>)}
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonInput label="Min salary (ZAR)" type="number" value={form.salaryMin} onIonInput={(e) => set({ salaryMin: e.detail.value! })} />
          </IonItem>

          <IonItem>
            <IonInput label="Max salary (ZAR)" type="number" value={form.salaryMax} onIonInput={(e) => set({ salaryMax: e.detail.value! })} />
          </IonItem>

          <IonItem>
            <IonInput label="Industry" placeholder="e.g. Construction, Mining" value={form.industry} onIonInput={(e) => set({ industry: e.detail.value! })} />
          </IonItem>

          <IonItem>
            <IonSelect label="Remote preference" value={form.remotePreference} onIonChange={(e) => set({ remotePreference: e.detail.value })}>
              <IonSelectOption value="OnSite">On-site</IonSelectOption>
              <IonSelectOption value="Hybrid">Hybrid</IonSelectOption>
              <IonSelectOption value="Remote">Remote</IonSelectOption>
              <IonSelectOption value="Any">Any</IonSelectOption>
            </IonSelect>
          </IonItem>

          <IonItem>
            <IonLabel>Willing to relocate</IonLabel>
            <IonToggle slot="end" checked={form.willingToRelocate} onIonChange={(e) => set({ willingToRelocate: e.detail.checked })} />
          </IonItem>
        </IonList>

        <IonButton expand="block" className="ion-margin-top" onClick={handleSave} disabled={updateProfile.isPending}>
          {updateProfile.isPending ? 'Saving…' : 'Save preferences'}
        </IonButton>
        <AppFooter />
      </IonContent>
      <IonToast isOpen={!!toast} message={toast?.msg} color={toast?.color} duration={2000} onDidDismiss={() => setToast(null)} />
    </IonPage>
  );
}
